import { pdf } from '@react-pdf/renderer';
import { format } from 'date-fns';
import { useCallback, useState } from 'react';
import {
  PurchaseOrderEventGroup,
  PurchaseOrderPaymentRow,
  PurchaseOrderPDF,
} from 'src/components/PurchaseOrderPDF';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { Content } from 'src/contexts/ContentContext';
import { PosDropdownItem } from 'src/core/POS/PosDropdown';
import { vars } from 'src/core/themes';
import { RotatingWrapper } from 'src/core/ui/AnimatingWrapper';
import { ProcessingIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { getEventPerformerVenue } from 'src/utils/eventWithDataUtils';
import {
  PurchaseOrderDetails,
  PurchaseOrderState,
  PurchasePaymentStatus,
} from 'src/WebApiController';

export const LaunchDownloadPurchaseOrder = ({
  purchaseOrder,
}: {
  purchaseOrder: PurchaseOrderDetails;
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const { loginContext } = useAppContext();
  const { data: catalogData } = useCatalogDataContext();

  const generateAndDownload = useCallback(async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const accountName = loginContext?.user?.activeAccount?.accountName ?? '';

      // Build event groups from ticket groups
      const eventGroupMap = new Map<string, PurchaseOrderEventGroup>();

      for (const tg of purchaseOrder.ticketGroups ?? []) {
        const viagVirtId = tg.viagVirtId;
        // Use global catalog context (miniCatalog on PurchaseOrderDetails is
        // always null because the backend passes getCatalog=false). Fall back
        // to miniCatalog just in case it's ever populated in the future.
        const { event: eventWithData, venue } = getEventPerformerVenue(
          viagVirtId,
          catalogData ?? purchaseOrder.miniCatalog
        );
        const event = eventWithData?.event;

        const eventKey = viagVirtId || `unknown-${tg.id}`;
        const eventName = event?.name ?? 'Unknown Event';
        const eventDate = event?.dates?.start ?? undefined;
        const venueName = venue?.name ?? undefined;
        const eventCity = venue?.city ?? undefined;
        const eventState = venue?.state ?? undefined;

        if (!eventGroupMap.has(eventKey)) {
          eventGroupMap.set(eventKey, {
            eventName,
            eventDate,
            venueName,
            eventCity,
            eventState,
            ticketRows: [],
            eventTotal: 0,
          });
        }

        const group = eventGroupMap.get(eventKey)!;
        const unitCost = tg.unitCst?.amt ?? 0;
        const rowTotal = unitCost * (tg.ticketCnt ?? 0);

        const listingNotes = (tg.seatTraits ?? [])
          .map((t) => t.listingNoteText)
          .filter(Boolean)
          .join('. ');

        group.ticketRows.push({
          qty: tg.ticketCnt ?? 0,
          section: tg.seating?.section ?? undefined,
          row: tg.seating?.row ?? undefined,
          seatFrom: tg.seating?.seatFr ?? undefined,
          seatTo: tg.seating?.seatTo ?? undefined,
          faceValue: tg.faceVal?.amt ?? undefined,
          cost: unitCost,
          fullyLoadedCost: tg.unitFullyLoadedCst?.amt ?? undefined,
          listingNotes: listingNotes || undefined,
        });

        group.eventTotal += rowTotal;
      }

      const eventGroups = Array.from(eventGroupMap.values());

      // Build payment rows
      const payments: PurchaseOrderPaymentRow[] = (
        purchaseOrder.payments ?? []
      ).map((pmt) => ({
        method: pmt.paymentMethod?.display
          ? `${pmt.paymentMethod.name ?? ''} ${pmt.paymentMethod.display}`.trim()
          : (pmt.paymentMethod?.name ?? 'Unknown'),
        date: pmt.paymentDate ?? pmt.paymentDueDate ?? undefined,
        amount: pmt.paymentAmount?.amt ?? 0,
      }));

      // Calculate summary values
      const totalTicketCost = purchaseOrder.totalCst?.amt ?? 0;
      const totalTaxes = (purchaseOrder.additionalCosts ?? [])
        .filter((c) => c.costType === 'Tax')
        .reduce((sum, c) => sum + (c.cost?.amt ?? 0), 0);
      const totalShipping = (purchaseOrder.additionalCosts ?? [])
        .filter((c) => c.costType === 'Shipping')
        .reduce((sum, c) => sum + (c.cost?.amt ?? 0), 0);
      const totalExpenses = (purchaseOrder.additionalCosts ?? [])
        .filter((c) => c.costType !== 'Tax' && c.costType !== 'Shipping')
        .reduce((sum, c) => sum + (c.cost?.amt ?? 0), 0);
      const poTotal =
        totalTicketCost + totalTaxes + totalShipping + totalExpenses;
      const totalPaid = purchaseOrder.totalPmt?.amt ?? 0;
      const balanceDue = purchaseOrder.outsBal?.amt ?? 0;

      // Payment method display
      const paymentMethodNames = (purchaseOrder.pmtMtdFullDisp ?? [])
        .map((pm) =>
          pm.display ? `${pm.name ?? ''} ${pm.display}`.trim() : (pm.name ?? '')
        )
        .filter(Boolean);
      const paymentMethodDisplay =
        paymentMethodNames.length > 0
          ? paymentMethodNames.join(', ')
          : undefined;

      const poState = purchaseOrder.poState ?? PurchaseOrderState.Active;
      const isPaid = purchaseOrder.pmtStat === PurchasePaymentStatus.Paid;

      const blob = await pdf(
        <PurchaseOrderPDF
          poStatus={poState}
          isPaid={isPaid}
          poDate={purchaseOrder.poDate ?? new Date().toISOString()}
          poNumber={`${purchaseOrder.id}`}
          externalPoNumber={purchaseOrder.vendOrdId ?? undefined}
          vendorName={purchaseOrder.vendor?.name ?? undefined}
          vendorId={
            purchaseOrder.vendor?.id ? `${purchaseOrder.vendor.id}` : undefined
          }
          vendorContact={purchaseOrder.vendAcc?.name ?? undefined}
          vendorPhone={purchaseOrder.vendAcc?.phoneNumber ?? undefined}
          vendorEmail={purchaseOrder.vendAcc?.email ?? undefined}
          shipToName={accountName}
          paymentMethod={paymentMethodDisplay}
          buyerRep={accountName}
          currency={purchaseOrder.currency ?? 'USD'}
          eventGroups={eventGroups}
          payments={payments}
          totalTickets={purchaseOrder.ticketCnt ?? 0}
          totalTicketCost={totalTicketCost}
          totalTaxes={totalTaxes}
          totalExpenses={totalExpenses}
          totalShipping={totalShipping}
          poTotal={poTotal}
          totalPaid={totalPaid}
          balanceDue={balanceDue}
          purchaseOrderNotes={purchaseOrder.privNotes ?? undefined}
        />
      ).toBlob();

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `PurchaseOrder_${purchaseOrder.id}_${format(
        new Date(),
        'yyyy-MM-dd'
      )}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating purchase order PDF:', error);
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, purchaseOrder, loginContext, catalogData]);

  return (
    <PosDropdownItem
      key="print-purchase-order"
      onClick={generateAndDownload}
      disabled={isLoading}
    >
      {isLoading ? (
        <RotatingWrapper>
          <ProcessingIcon fill={vars.color.textBrand} />
        </RotatingWrapper>
      ) : (
        <Content id={ContentId.PrintPurchaseOrder} />
      )}
    </PosDropdownItem>
  );
};
